import { useState } from "react";
import { createPortal } from "react-dom";
import type { Call } from "@entities/call";
import { fromSelf, markOf, Tile } from "@entities/user";
import { useMe, useSession } from "@entities/session";
import { StatusDialog } from "@features/custom-status";
import type { Uuid } from "@shared/api";
import {
  MAX_MIC_VOLUME,
  MAX_VOLUME,
  MASTER_KEY,
  chooseDevice,
  inputProfile,
  setInputProfile,
  setVolume,
  speakerSwitchable,
  useAudioTuning,
  useDevices,
  useMicrophone,
  useVolumes,
  type InputProfile,
} from "@shared/media";
import { setMicGain } from "@shared/denoiser";
import { Hint } from "@shared/ui/bits";
import { Menu, SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import { Slider } from "@shared/ui/slider";
import type { SettingsTab } from "@widgets/user-settings";
import { SelfPopout } from "@widgets/profile-card";
import {
  IconChevronDown,
  IconMic,
  IconMicOff,
  IconSettings,
  IconSound,
  IconSoundOff,
} from "@shared/ui/icons";

/**
 * Полоса себя: кто вошёл и два выключателя звука.
 *
 * Выключатели стоят здесь, а не только в полосе идущего звонка: их ищут
 * глазами в одном и том же месте — внизу, у своего имени, — и до того, как
 * разговор начался. Микрофон при этом величина глобальная, а не свойство
 * звонка: нажатое заранее применяется при входе в канал.
 *
 * У каждого выключателя своё меню — устройство и громкость. Это тот же выбор,
 * что в настройках, и сделан он теми же действиями: второго места, где
 * хранится «какой микрофон», нет.
 */
export function SelfBar({
  call,
  onSettings,
  onOpenProfile,
}: {
  call: Call;
  onSettings: (open?: SettingsTab) => void;
  /** Свой полный профиль — окном. */
  onOpenProfile: (userId: Uuid) => void;
}) {
  const me = useMe();
  const person = fromSelf(me);

  /*
   * До входа в канал показывается выбранное заранее, в канале — то, что на
   * самом деле уходит в него. Расходятся они ровно в одном случае: устройство
   * отказало, и тогда честнее показать молчание.
   */
  const wanted = useMicrophone((state) => state.on);
  // С выключенным звуком микрофон молчит и вне звонка — как в Discord.
  const micOn = call.state === "live" ? call.microphone : wanted && !call.deafened;

  /** Какое меню открыто и в какой точке. `null` — ни одного. */
  const [menu, setMenu] = useState<{ kind: "mic" | "sound"; x: number; y: number } | null>(null);
  /** Своя карточка открыта — от чего она встаёт. */
  const [selfAt, setSelfAt] = useState<HTMLElement | null>(null);
  /** Окно статуса открыто — с какой подсказкой в поле. */
  const [statusing, setStatusing] = useState<string | null>(null);
  const refreshMe = useSession((state) => state.refreshMe);

  /*
   * Меню отсчитывается от нижнего края кнопки: полоса стоит внизу экрана, и
   * Radix сам откинет его вверх, когда снизу места нет.
   */
  const openFrom = (kind: "mic" | "sound") => (event: React.MouseEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setMenu({ kind, x: box.left, y: box.bottom + 6 });
  };

  return (
    <div className="self-bar glass-host">
      {/*
        Себя — кнопкой, как в Discord: своя карточка с профилем, присутствием и
        статусом, не уходя в настройки. Повторный щелчок её закрывает.
      */}
      <button
        type="button"
        className="self-who grow"
        onClick={(event) => {
          const button = event.currentTarget;
          setSelfAt((open) => (open ? null : button));
        }}
        aria-label="Управление профилем и статусом"
        aria-expanded={!!selfAt}
      >
        <Tile person={person} size={38} mark={markOf(me.status_presence)} />
        <span className="grow" style={{ minWidth: 0 }}>
          <span className="self-name ellipsis">{person.name}</span>
          <span className="tiny muted ellipsis">
            {me.status_emoji && <span className="status-mark">{me.status_emoji}</span>}
            {me.status_text ||
              (me.status_emoji ? "" : me.status_presence === "invisible" ? "вас не видят" : me.handle)}
          </span>
        </span>
      </button>

      {/*
        Кнопка и стрелка — одна плашка, разделённая волоском, а не две кнопки
        подряд. Иначе в полосе оказывается пять круглых предметов в ряд, и на
        узкой колонке от имени не остаётся ничего.
      */}
      <div className="self-switch">
        <Hint label={micOn ? "Выключить микрофон" : "Включить микрофон"}>
          <button
            type="button"
            className={`self-switch-btn ${micOn ? "" : "off"}`}
            onClick={() => void call.toggleMicrophone()}
          >
            {micOn ? <IconMic size={15} /> : <IconMicOff size={15} />}
          </button>
        </Hint>

        <Hint label="Микрофон и устройство ввода">
          <button type="button" className="self-switch-more" onClick={openFrom("mic")}>
            <IconChevronDown size={11} />
          </button>
        </Hint>
      </div>

      <div className="self-switch">
        <Hint label={call.deafened ? "Включить звук" : "Выключить звук"}>
          <button
            type="button"
            className={`self-switch-btn ${call.deafened ? "off" : ""}`}
            onClick={call.toggleDeafen}
          >
            {call.deafened ? <IconSoundOff size={15} /> : <IconSound size={15} />}
          </button>
        </Hint>

        <Hint label="Звук и устройство вывода">
          <button type="button" className="self-switch-more" onClick={openFrom("sound")}>
            <IconChevronDown size={11} />
          </button>
        </Hint>
      </div>

      <Hint label="Настройки">
        <button type="button" className="icon-btn" onClick={() => onSettings()}>
          <IconSettings size={15} />
        </button>
      </Hint>

      {/*
        Меню уезжает в корень страницы, а не остаётся здесь.

        Оно отсчитывается от метки с `position: fixed`, а полоса себя —
        стеклянная: `backdrop-filter` делает её точкой отсчёта для всего
        `fixed` внутри, и меню, посчитанное от окна, оказывалось на полтысячи
        точек ниже экрана. Со стороны это выглядело как «меню не открывается».
      */}
      {menu?.kind === "mic" &&
        createPortal(
          <MicMenu at={menu} onSettings={onSettings} onClose={() => setMenu(null)} />,
          document.body,
        )}

      {menu?.kind === "sound" &&
        createPortal(
          <SoundMenu at={menu} onSettings={onSettings} onClose={() => setMenu(null)} />,
          document.body,
        )}

      {selfAt && (
        <SelfPopout
          anchor={selfAt}
          onClose={() => setSelfAt(null)}
          onOpenFull={onOpenProfile}
          onEditProfile={() => onSettings("profile")}
          onSetStatus={setStatusing}
        />
      )}

      {statusing !== null && (
        <StatusDialog me={me} prompt={statusing} onSaved={() => void refreshMe()} onClose={() => setStatusing(null)} />
      )}
    </div>
  );
}

/**
 * Список устройств пунктами меню.
 *
 * Место под галочку держат все пункты, а не только отмеченный: иначе выбор
 * другого устройства сдвигает подписи, и глаз ловит движение вместо смысла.
 */
function deviceItems(
  devices: { id: string; label: string }[],
  chosen: string | null,
  onPick: (id: string | null) => void,
): MenuItem[] {
  const items: MenuItem[] = [
    {
      label: devices.length ? "Системное по умолчанию" : "Устройств не видно",
      choice: true,
      on: chosen === null,
      onPick: devices.length ? () => onPick(null) : undefined,
    },
  ];

  for (const device of devices) {
    items.push({
      label: device.label,
      choice: true,
      on: chosen === device.id,
      onPick: () => onPick(device.id),
    });
  }

  return items;
}

/** Профили ввода — подпись и что делает, второй строкой. */
const PROFILES: { profile: InputProfile; label: string; note: string }[] = [
  { profile: "isolation", label: "Изоляция голоса", note: "Убирает шум и эхо вокруг голоса" },
  { profile: "studio", label: "Студия", note: "Микрофон как есть, без обработки" },
  { profile: "custom", label: "Пользовательский", note: "Своя настройка из «Голоса и видео»" },
];

/**
 * Меню микрофона — как в Discord: устройство ввода, профиль ввода,
 * громкость, «Настройки голоса». Заголовка нет, выбранное — второй строкой
 * под подписью.
 *
 * Список устройств спрашивается только пока меню открыто: перечитывать его
 * всё время работы приложения незачем, а сам опрос стоит обращения к системе.
 */
function MicMenu({
  at,
  onSettings,
  onClose,
}: {
  at: { x: number; y: number };
  onSettings: (open?: SettingsTab) => void;
  onClose: () => void;
}) {
  const { devices, choice } = useDevices();
  const [tuning, setTuning] = useAudioTuning();
  const profile = inputProfile(tuning);

  const items: MenuItem[] = [
    {
      label: "Устройство ввода",
      note: devices.microphones.find((item) => item.id === choice.microphone)?.label ?? "Системное по умолчанию",
      items: deviceItems(devices.microphones, choice.microphone, (id) =>
        chooseDevice("microphone", id),
      ),
    },
    {
      label: "Профиль ввода",
      note: PROFILES.find((item) => item.profile === profile)!.label,
      items: PROFILES.map((item) => ({
        label: item.label,
        note: item.note,
        choice: true,
        on: profile === item.profile,
        // Своей настройки ещё не было — вернуть нечего, её заводят в настройках голоса.
        onPick: () => {
          if (!setInputProfile(item.profile)) onSettings("audio");
        },
      })),
    },
    SEPARATOR,
    {
      label: "Громкость микрофона",
      block: (
        <Level
          title="Громкость микрофона"
          value={tuning.micVolume}
          max={MAX_MIC_VOLUME}
          onChange={(value) => {
            setTuning({ ...tuning, micVolume: value });
            // Звонок слышит ползунок сразу, как и в настройках, а не со следующего входа.
            setMicGain(value);
          }}
        />
      ),
    },
    SEPARATOR,
    { label: "Настройки голоса", icon: IconSettings, onPick: () => onSettings("audio") },
  ];

  return <Menu at={at} items={items} onClose={onClose} />;
}

/** Меню звука — как в Discord: устройство вывода, громкость, «Настройки голоса». */
function SoundMenu({
  at,
  onSettings,
  onClose,
}: {
  at: { x: number; y: number };
  onSettings: (open?: SettingsTab) => void;
  onClose: () => void;
}) {
  const { devices, choice } = useDevices();
  const volumes = useVolumes();
  const master = volumes[MASTER_KEY] ?? 1;

  /*
   * Вывод переключается не везде: в Safari менять его нечем вовсе. Тогда
   * пункт остаётся, но говорит правду — иначе человек ищет то, чего в его
   * браузере нет.
   */
  const items: MenuItem[] = [
    speakerSwitchable()
      ? {
          label: "Устройство вывода",
          note: devices.speakers.find((item) => item.id === choice.speaker)?.label ?? "Системное по умолчанию",
          items: deviceItems(devices.speakers, choice.speaker, (id) => chooseDevice("speaker", id)),
        }
      : { label: "Устройство вывода", note: "Браузер не переключает вывод", kind: "plan" },
    SEPARATOR,
    {
      label: "Громкость звука",
      block: (
        <Level
          title="Громкость звука"
          value={master}
          max={MAX_VOLUME}
          onChange={(value) => setVolume(MASTER_KEY, value)}
        />
      ),
    },
    SEPARATOR,
    { label: "Настройки голоса", icon: IconSettings, onPick: () => onSettings("audio") },
  ];

  return <Menu at={at} items={items} onClose={onClose} />;
}

/**
 * Ползунок между пунктами меню, как в Discord: подпись в размер пунктов, под
 * ней дорожка. Процент справа остаётся — у нас громкость идёт до двухсот, и
 * без числа не видно, где кончаются сто.
 *
 * Двойной щелчок возвращает сто процентов: искать их на глаз по ползунку
 * долго. Он висит на обёртке, а не на бегунке, — попасть по четырнадцати
 * точкам дважды подряд труднее, чем по всей дорожке.
 */
function Level({
  title,
  value,
  max,
  onChange,
}: {
  title: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const percent = Math.round(value * 100);

  return (
    <div className="vol menu-level">
      <div className="vol-row">
        <span className="grow">{title}</span>
        <span className={`vol-value ${percent > 100 ? "up" : ""}`}>{percent} %</span>
      </div>

      <div onDoubleClick={() => onChange(1)}>
        <Slider
          min={0}
          max={max}
          step={0.05}
          value={[value]}
          loud={percent > 100}
          // Сто процентов лежат там, где начинается усиление: до них ручка
          // идёт без всяких «×2».
          mark={1 / max}
          aria-label={title}
          onValueChange={([next]) => onChange(next)}
        />
      </div>
    </div>
  );
}
